import {
  TeContainer,
  TeContainerSlim,
  TeInputText,
  TeLabel,
  TePopover,
  TeText,
} from '@topcon/tuicl-react';
import { TeChangeEvent } from '@topcon/tuicl';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PlaceholderContainer } from '@topcon/topcon-utils';

import SelectableBusinessPartner from '../../SelectableBusinessPartner';
import { useCustomerWithTopconNumber } from '../../../pages/Customers/customers.queries';
import useDebounce from '../../../hooks/useDebounce';
import { CustomerBody } from '../../../api/generated/pm';
import { zServiceAgreementWithCustomerPostBody } from '../../../api/generated/pm/zod.gen';
import {
  stripOrganizationNumberFormatting,
  formatOrganizationNumber,
  isValidOrganizationNumber,
} from '../../../utils/organizationNumberFormatter';

/** The dealer's own reference for this customer — `customerNumber` on the API. */
const CUSTOMER_NUMBER_FIELD = 'providerCustomerNumber';
const CUSTOMER_NUMBER_MAX_LENGTH =
  zServiceAgreementWithCustomerPostBody.shape.customerNumber.unwrap().maxLength ?? undefined;

interface SearchCustomerProps {
  selectedCustomer: CustomerBody | undefined;
  setSelectedCustomer: (customer?: CustomerBody) => void;
  providerCustomerNumber: string;
  setProviderCustomerNumber: (value: string) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
}

const SelectExistingCustomer = ({
  selectedCustomer,
  setSelectedCustomer,
  providerCustomerNumber,
  setProviderCustomerNumber,
  searchValue,
  setSearchValue,
}: SearchCustomerProps) => {
  const { t } = useTranslation();
  const strippedSearchValue = stripOrganizationNumberFormatting(searchValue);
  const debounceSearchValue = useDebounce(strippedSearchValue);
  const { data: customer, isLoading: isLoadingCustomers } =
    useCustomerWithTopconNumber(debounceSearchValue);

  const handleSearchValueChange = (e: TeChangeEvent) => {
    const rawValue = (e.target as HTMLInputElement).value;
    const digitsOnly = stripOrganizationNumberFormatting(rawValue);

    // Limit to 12 digits
    if (digitsOnly.length <= 12) {
      const formatted = formatOrganizationNumber(digitsOnly);
      if (formatted === searchValue) return;
      // A new search hides the selected card; drop its selection and number so neither is sent unseen.
      setSelectedCustomer(undefined);
      setProviderCustomerNumber('');
      setSearchValue(formatted);
    }
  };

  const handleSelectBusinessPartner = (customer: CustomerBody) => {
    setProviderCustomerNumber('');
    setSelectedCustomer(selectedCustomer?.id !== customer.id ? customer : undefined);
  };

  const NoContentPlaceholder = ({ text }: { text: string }) => (
    <TextWrapper fill grow alignItems="center" justifyContent="center">
      <TeText weight={400} size={12} color="text.secondary">
        {text}
      </TeText>
    </TextWrapper>
  );

  const isSearchValueValid = isValidOrganizationNumber(searchValue);

  const renderCustomer = (customer: CustomerBody) => {
    const address = `${customer?.companyAddress.addressLineOne}, ${customer?.companyAddress.zipPostalCode} ${customer?.companyAddress.city} ${customer?.companyAddress.stateProvinceRegion}, ${customer?.companyAddress.country}`;
    const isSelected = selectedCustomer?.id === customer.id;
    return (
      <TeContainerSlim fill grow padding="m">
        <SelectableBusinessPartner
          key={customer.id}
          onClick={() => handleSelectBusinessPartner(customer)}
          icon="business"
          title={customer.name}
          subtitle={address}
          active={isSelected}
          selectCtaType="checkbox"
        >
          {isSelected && (
            <TeContainerSlim column fill gap="xs" padding={['xs', 0, 0, 0]}>
              <TeLabel
                text={t('customerNumber.label')}
                subtext={t('customerNumber.optional')}
                icon="info"
                name={CUSTOMER_NUMBER_FIELD}
              >
                <TePopover variant="secondary" operation="hover" placement="top">
                  <TeContainerSlim padding="xs">
                    <TeText color="text.inverse" size={12}>
                      {t('customerNumber.tooltip')}
                    </TeText>
                  </TeContainerSlim>
                </TePopover>
              </TeLabel>
              <TeInputText
                name={CUSTOMER_NUMBER_FIELD}
                maxLength={CUSTOMER_NUMBER_MAX_LENGTH}
                size="s"
                grow
                placeholder={t('customerNumber.placeholder')}
                value={providerCustomerNumber}
                techange={(e) => setProviderCustomerNumber((e.target as HTMLInputElement).value)}
              />
            </TeContainerSlim>
          )}
        </SelectableBusinessPartner>
      </TeContainerSlim>
    );
  };

  return (
    <TeContainer column fill grow justifyContent="center" gap="xl" padding={0}>
      <TeContainerSlim
        fill
        column
        justifyContent="center"
        padding={['l', 0, 0, 0]}
        alignItems="center"
        gap="xs"
      >
        <TeText weight={700} size={20} alignment="center">
          {t('customers.existing.title')}
        </TeText>
        <TeText weight={400} size={14} alignment="center" color="text.secondary">
          {t('customers.existing.description')}
        </TeText>
      </TeContainerSlim>
      <TeContainerSlim fill column justifyContent="center" alignItems={'center'}>
        <TeInputText
          placeholder={t('customers.existing.searchCustomerPlaceholder')}
          icon="search"
          size={'s'}
          valid={isSearchValueValid}
          techange={handleSearchValueChange}
          value={searchValue}
        />
      </TeContainerSlim>
      <ResultsContainer
        fill
        grow
        backgroundColor="background.tertiary"
        padding={0}
        alignItems="flex-start"
        overflow="auto"
        wrap={true}
      >
        {debounceSearchValue === '' && (
          <NoContentPlaceholder text={t('customers.existing.placeholder')} />
        )}

        {debounceSearchValue !== '' && isLoadingCustomers && (
          // <TeContainerSlim fill grow alignItems="center" justifyContent="center" padding={[0, 0]}>
          <PlaceholderContainer grow hasLoader text={t('customers.loading')} />
          // </TeContainerSlim>
        )}

        {debounceSearchValue !== '' && !isLoadingCustomers && !customer && (
          <NoContentPlaceholder text={t('customers.noSearchResults')} />
        )}

        {debounceSearchValue !== '' && !isLoadingCustomers && customer && renderCustomer(customer)}
      </ResultsContainer>
    </TeContainer>
  );
};

const TextWrapper = styled(TeContainerSlim)`
  height: 100% !important;
`;

// Grows past the empty-state height so the selected card's customer number input fits.
const ResultsContainer = styled(TeContainer)`
  min-height: 115px;
`;

export default SelectExistingCustomer;
